import React from 'react';
import { Spinner } from 'reactstrap';
import { useTranslator, formatLongDate } from '@front-activerse/common-components';
import { DESCRIPTION_KEYS } from './helpers';
import type { ActiveProductItem } from './types';

interface Props {
  product: ActiveProductItem;
  /** Hidden inside the native app (Apple guidelines): there the user is sent to the website. */
  showManageButton: boolean;
  isManaging: boolean;
  onManage: (subscriptionId: number) => void;
}

const AccessCard: React.FC<Props> = ({ product, showManageButton, isManaging, onManage }) => {
  const { t } = useTranslator();

  const canManage =
    showManageButton && product.canManageSubscription && product.subscriptionId !== undefined;

  const description = t(DESCRIPTION_KEYS[product.descriptionVariant], {
    date: product.date ? formatLongDate(product.date) : '',
  });

  return (
    <div className="access-card" data-testid={`access-card-${product.key}`}>
      <div className="access-card__info">
        {product.badgeKey && (
          <span
            className="access-card__badge"
            data-test-id="access_store_info"
            data-testid="access-card-badge"
          >
            {t(product.badgeKey)}
          </span>
        )}
        <div className="access-card__details">
          <div className="access-card__title" data-testid="access-card-title">
            {t(product.titleKey)}
          </div>
          {product.isTrial && (
            <div className="access-card__subtitle" data-testid="access-card-subtitle">
              {t('accessCard.trialPeriod.subtitle')}
            </div>
          )}
          <div
            className="access-card__description"
            data-test-id="access_renewal_time_text"
            data-testid="access-card-description"
          >
            {description}
          </div>
        </div>
      </div>

      {canManage && (
        <div className="access-card__manage">
          <button
            type="button"
            className="access-card__manage-button"
            onClick={() => onManage(product.subscriptionId as number)}
            disabled={isManaging}
            data-test-id="access_manage_subscription_button"
            data-testid="access-card-manage"
          >
            {isManaging && <Spinner size="sm" className="me-1" />}
            {t('accessCard.manageSubscription.button')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AccessCard;
